import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, RefreshCw } from 'lucide-react';

const C = {
  bg: '#FDFBF7', card: '#FFFFFF', border: '#E8E3DB',
  text: '#1A1A18', muted: '#9A9590', accent: '#C67A5C', green: '#5A8C6A',
};

class ErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback
        ? this.props.fallback(this.state.error, () => this.setState({ hasError: false, error: null }))
        : <ErrorFallback onRetry={() => this.setState({ hasError: false, error: null })} />;
    }
    return this.props.children;
  }
}

function ErrorFallback({ onRetry }) {
  const navigate = useNavigate();
  return (
    <div data-testid="error-boundary-fallback" style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 360 }}>
        <div style={{ width: 56, height: 56, borderRadius: 16, background: `${C.accent}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <AlertTriangle style={{ width: 28, height: 28, color: C.accent }} />
        </div>
        <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 22, color: C.text, margin: '0 0 8px' }}>Something went wrong</h2>
        <p style={{ fontSize: 14, color: C.muted, fontFamily: 'var(--font-sans)', margin: '0 0 24px', lineHeight: 1.5 }}>
          This page ran into an issue. Try going back or refreshing.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button
            data-testid="error-go-back"
            onClick={() => navigate(-1)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px',
              borderRadius: 24, border: `1px solid ${C.border}`, background: C.card,
              color: C.text, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font-sans)',
            }}
          >
            <ArrowLeft style={{ width: 16, height: 16 }} /> Go back
          </button>
          <button
            data-testid="error-retry"
            onClick={onRetry}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px',
              borderRadius: 24, border: 'none', background: C.green,
              color: '#FFF', fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font-sans)',
            }}
          >
            <RefreshCw style={{ width: 16, height: 16 }} /> Retry
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ErrorBoundary({ children, fallback }) {
  return (
    <ErrorBoundaryInner fallback={fallback}>
      {children}
    </ErrorBoundaryInner>
  );
}
